import type { SupportedLang } from "@/lib/translations.functions";

type CountCopy = {
  discoveriesAcross: (discoveries: string, series: string) => string;
  episodes: (count: number) => string;
  minutes: (count: number) => string;
  readFirst: string;
};

export type HomepageNewsletterCopy = {
  eyebrow: string;
  headline: string;
  details: string;
  cadence: string;
  closing: string;
  promise: string;
  cta: string;
  noSpam: string;
};

const NEWSLETTER_COPY: Record<SupportedLang, HomepageNewsletterCopy> = {
  en: {
    eyebrow: "The Smartasaurus Sunday Letter",
    headline: "There’s a lot you were never taught.",
    details: "Why your body does things nobody explained. How people survive situations that should kill them. The strange engineering hiding in ordinary objects. The history that got simplified. The “facts” we repeat that aren’t actually true.",
    cadence: "Every Sunday, we pick the best ones we found, check the evidence, and send them to you.",
    closing: "If you like knowing how the world really works, you’ll like Sundays here.",
    promise: "Free. 5 minutes. Sources included.",
    cta: "Send me Sunday’s letter",
    noSpam: "No spam. Unsubscribe anytime.",
  },
  fr: {
    eyebrow: "La lettre du dimanche de Smartasaurus",
    headline: "Il y a tant de choses qu’on ne vous a jamais apprises.",
    details: "Pourquoi votre corps fait des choses que personne ne vous a jamais expliquées. Comment certains survivent à des situations qui auraient dû les tuer. L’ingéniosité insoupçonnée des objets du quotidien. L’histoire qu’on a simplifiée. Et ces « vérités » que tout le monde répète alors qu’elles sont fausses.",
    cadence: "Chaque dimanche, nous choisissons les meilleures découvertes de la semaine, vérifions les preuves et vous les envoyons.",
    closing: "Si vous aimez comprendre comment le monde fonctionne vraiment, vous allez aimer vos dimanches ici.",
    promise: "Gratuit. 5 minutes. Sources à l’appui.",
    cta: "Recevoir la lettre du dimanche",
    noSpam: "Aucun spam. Désabonnez-vous à tout moment.",
  },
  es: {
    eyebrow: "La carta dominical de Smartasaurus",
    headline: "Hay muchísimo que nunca te enseñaron.",
    details: "Por qué tu cuerpo hace cosas que nadie te explicó. Cómo hay personas que sobreviven a situaciones que deberían matarlas. La ingeniería sorprendente que esconden los objetos cotidianos. La historia que nos contaron a medias. Y esos «hechos» que repetimos aunque no sean ciertos.",
    cadence: "Cada domingo elegimos los mejores hallazgos de la semana, comprobamos las pruebas y te los enviamos.",
    closing: "Si te gusta entender cómo funciona de verdad el mundo, te van a gustar los domingos aquí.",
    promise: "Gratis. 5 minutos. Con fuentes.",
    cta: "Envíame la carta del domingo",
    noSpam: "Sin spam. Cancela cuando quieras.",
  },
  pt: {
    eyebrow: "A carta de domingo do Smartasaurus",
    headline: "Há muita coisa que nunca ensinaram a você.",
    details: "Por que seu corpo faz coisas que ninguém nunca explicou. Como pessoas sobrevivem a situações que deveriam matá-las. A engenharia surpreendente escondida em objetos comuns. A história que foi simplificada. E os “fatos” que repetimos sem serem verdadeiros.",
    cadence: "Todo domingo, escolhemos as melhores descobertas da semana, conferimos as evidências e enviamos para você.",
    closing: "Se você gosta de entender como o mundo realmente funciona, vai gostar dos domingos por aqui.",
    promise: "Grátis. 5 minutos. Fontes incluídas.",
    cta: "Quero receber a carta de domingo",
    noSpam: "Sem spam. Cancele quando quiser.",
  },
  de: {
    eyebrow: "Der Smartasaurus-Sonntagsbrief",
    headline: "Es gibt so vieles, das dir nie beigebracht wurde.",
    details: "Warum dein Körper Dinge tut, die dir nie jemand erklärt hat. Wie Menschen Situationen überleben, die sie eigentlich nicht überleben dürften. Welche verblüffende Technik in alltäglichen Gegenständen steckt. Welche Teile der Geschichte vereinfacht wurden. Und welche „Fakten“ wir ständig wiederholen, obwohl sie gar nicht stimmen.",
    cadence: "Jeden Sonntag wählen wir die besten Entdeckungen der Woche aus, prüfen die Belege und schicken sie dir.",
    closing: "Wenn du gern verstehst, wie die Welt wirklich funktioniert, wirst du dich auf Sonntage mit uns freuen.",
    promise: "Kostenlos. 5 Minuten. Mit Quellen.",
    cta: "Schick mir den Sonntagsbrief",
    noSpam: "Kein Spam. Jederzeit abbestellbar.",
  },
  sv: {
    eyebrow: "Smartasaurus söndagsbrev",
    headline: "Det finns så mycket du aldrig fick lära dig.",
    details: "Varför din kropp gör saker som ingen har förklarat. Hur människor överlever situationer som borde ha dödat dem. Den oväntade ingenjörskonsten i vanliga föremål. Historien som förenklades. Och alla ”fakta” vi upprepar trots att de inte stämmer.",
    cadence: "Varje söndag väljer vi ut veckans bästa upptäckter, granskar bevisen och skickar dem till dig.",
    closing: "Om du gillar att förstå hur världen faktiskt fungerar kommer du att gilla söndagar här.",
    promise: "Gratis. 5 minuter. Källor ingår.",
    cta: "Skicka söndagsbrevet till mig",
    noSpam: "Ingen skräppost. Avsluta när du vill.",
  },
  hi: {
    eyebrow: "Smartasaurus का संडे लेटर",
    headline: "बहुत कुछ ऐसा है जो आपको कभी सिखाया ही नहीं गया।",
    details: "आपका शरीर ऐसी हरकतें क्यों करता है जिनकी वजह किसी ने नहीं बताई। लोग उन हालात से कैसे बच निकलते हैं जिनमें बचना नामुमकिन लगता है। रोज़मर्रा की चीज़ों में छिपी कमाल की इंजीनियरिंग। इतिहास की वे बातें जिन्हें आसान बनाते-बनाते अधूरा कर दिया गया। और वे “तथ्य” जिन्हें हम दोहराते रहते हैं, जबकि वे सच ही नहीं हैं।",
    cadence: "हर रविवार हम उस हफ़्ते की सबसे दिलचस्प खोजें चुनते हैं, उनके सबूत जाँचते हैं और आपको भेजते हैं।",
    closing: "अगर आपको यह जानना पसंद है कि दुनिया असल में कैसे काम करती है, तो यहाँ के रविवार आपको पसंद आएँगे।",
    promise: "मुफ़्त। 5 मिनट। स्रोत शामिल हैं।",
    cta: "रविवार की चिट्ठी भेजें",
    noSpam: "कोई स्पैम नहीं। जब चाहें सदस्यता छोड़ें।",
  },
  zh: {
    eyebrow: "Smartasaurus 周日来信",
    headline: "有很多事，从来没人教过你。",
    details: "身体为什么会有那些没人解释过的反应。人们如何从本该致命的险境中活下来。普通物品里藏着怎样出人意料的工程设计。被简化的历史。还有那些我们反复念叨、其实并不正确的“事实”。",
    cadence: "每周日，我们会选出这一周最精彩的发现，核查证据，再发送给你。",
    closing: "如果你喜欢了解世界真正的运作方式，你会喜欢这里的每个周日。",
    promise: "免费阅读。只需 5 分钟。附有资料来源。",
    cta: "给我发送周日来信",
    noSpam: "绝无垃圾邮件，可随时退订。",
  },
  ru: {
    eyebrow: "Воскресное письмо Smartasaurus",
    headline: "Есть столько всего, о чём вам никогда не рассказывали.",
    details: "Почему тело делает то, чему никто не дал объяснения. Как люди выживают там, где выжить казалось невозможно. Какая удивительная инженерия скрывается в обычных вещах. Что упростили, пересказывая историю. И какие «факты» мы повторяем, хотя они не соответствуют действительности.",
    cadence: "Каждое воскресенье мы выбираем лучшие находки недели, проверяем доказательства и отправляем их вам.",
    closing: "Если вам интересно, как на самом деле устроен мир, вам понравятся наши воскресенья.",
    promise: "Бесплатно. 5 минут. Со ссылками на источники.",
    cta: "Получать воскресное письмо",
    noSpam: "Без спама. Отписаться можно в любой момент.",
  },
  ar: {
    eyebrow: "رسالة الأحد من سمارتاسورس",
    headline: "هناك الكثير مما لم يعلّمك إياه أحد.",
    details: "لماذا يفعل جسمك أشياء لم يشرحها لك أحد. كيف ينجو أشخاص من مواقف كان يُفترض أن تودي بحياتهم. الهندسة المدهشة المختبئة في الأشياء العادية. أجزاء التاريخ التي اختُصرت حتى تغيّر معناها. و«الحقائق» التي نكررها رغم أنها غير صحيحة.",
    cadence: "كل يوم أحد، نختار أفضل ما اكتشفناه خلال الأسبوع، ونتحقق من الأدلة، ثم نرسله إليك.",
    closing: "إذا كنت تحب أن تعرف كيف يعمل العالم حقًا، فستحب أيام الأحد هنا.",
    promise: "مجانًا. 5 دقائق. المصادر مرفقة.",
    cta: "أرسلوا إليّ رسالة الأحد",
    noSpam: "من دون رسائل مزعجة. يمكنك إلغاء الاشتراك في أي وقت.",
  },
};

const COUNT_COPY: Record<SupportedLang, CountCopy> = {
  en: {
    discoveriesAcross: (discoveries, series) => `${discoveries} discoveries across ${series} series - every one of them real.`,
    episodes: (count) => `${count} episodes`,
    minutes: (count) => `~${count} min`,
    readFirst: "Read episode 1",
  },
  fr: {
    discoveriesAcross: (discoveries, series) => `${discoveries} découvertes dans ${series} séries - toutes bien réelles.`,
    episodes: (count) => `${count} épisodes`,
    minutes: (count) => `~${count} min`,
    readFirst: "Lire l’épisode 1",
  },
  es: {
    discoveriesAcross: (discoveries, series) => `${discoveries} descubrimientos en ${series} series - todos reales.`,
    episodes: (count) => `${count} episodios`,
    minutes: (count) => `~${count} min`,
    readFirst: "Leer el episodio 1",
  },
  pt: {
    discoveriesAcross: (discoveries, series) => `${discoveries} descobertas em ${series} séries - todas reais.`,
    episodes: (count) => `${count} episódios`,
    minutes: (count) => `~${count} min`,
    readFirst: "Ler o episódio 1",
  },
  de: {
    discoveriesAcross: (discoveries, series) => `${discoveries} Entdeckungen in ${series} Serien - alle davon wahr.`,
    episodes: (count) => `${count} Folgen`,
    minutes: (count) => `ca. ${count} Min.`,
    readFirst: "Folge 1 lesen",
  },
  sv: {
    discoveriesAcross: (discoveries, series) => `${discoveries} upptäckter i ${series} serier - allt är på riktigt.`,
    episodes: (count) => `${count} avsnitt`,
    minutes: (count) => `ca ${count} min`,
    readFirst: "Läs avsnitt 1",
  },
  hi: {
    discoveriesAcross: (discoveries, series) => `${series} शृंखलाओं में ${discoveries} खोजें - हर एक सच है।`,
    episodes: (count) => `${count} कड़ियाँ`,
    minutes: (count) => `लगभग ${count} मिनट`,
    readFirst: "पहली कड़ी पढ़ें",
  },
  zh: {
    discoveriesAcross: (discoveries, series) => `${series} 个系列，${discoveries} 项发现 - 全都是真实的。`,
    episodes: (count) => `${count} 集`,
    minutes: (count) => `约 ${count} 分钟`,
    readFirst: "阅读第 1 集",
  },
  ru: {
    discoveriesAcross: (discoveries, series) => `${discoveries} открытий в ${series} сериях - и всё это правда.`,
    episodes: (count) => `${count} выпусков`,
    minutes: (count) => `около ${count} мин`,
    readFirst: "Читать выпуск 1",
  },
  ar: {
    discoveriesAcross: (discoveries, series) => `${discoveries} اكتشافًا ضمن ${series} سلاسل - وكلها حقيقية.`,
    episodes: (count) => `${count} حلقة`,
    minutes: (count) => `نحو ${count} دقيقة`,
    readFirst: "اقرأ الحلقة الأولى",
  },
};

const RANK_NAMES: Record<SupportedLang, Record<string, string>> = {
  en: { Scroller: "Explorer", "Lab Analyst": "Lab Analyst", "Field Researcher": "Field Researcher", "Apex Smartasaurus": "Apex Smartasaurus" },
  fr: { Scroller: "Explorateur", "Lab Analyst": "Analyste de laboratoire", "Field Researcher": "Chercheur de terrain", "Apex Smartasaurus": "Smartasaurus suprême" },
  es: { Scroller: "Explorador", "Lab Analyst": "Analista de laboratorio", "Field Researcher": "Investigador de campo", "Apex Smartasaurus": "Smartasaurus supremo" },
  pt: { Scroller: "Explorador", "Lab Analyst": "Analista de laboratório", "Field Researcher": "Pesquisador de campo", "Apex Smartasaurus": "Smartasaurus supremo" },
  de: { Scroller: "Entdecker", "Lab Analyst": "Laboranalyst", "Field Researcher": "Feldforscher", "Apex Smartasaurus": "Apex-Smartasaurus" },
  sv: { Scroller: "Upptäckare", "Lab Analyst": "Laboratorieanalytiker", "Field Researcher": "Fältforskare", "Apex Smartasaurus": "Smartasaurus på toppnivå" },
  hi: { Scroller: "खोजी", "Lab Analyst": "प्रयोगशाला विश्लेषक", "Field Researcher": "मैदानी शोधकर्ता", "Apex Smartasaurus": "सर्वोच्च स्मार्टासॉरस" },
  zh: { Scroller: "探索者", "Lab Analyst": "实验室分析员", "Field Researcher": "实地研究员", "Apex Smartasaurus": "顶尖 Smartasaurus" },
  ru: { Scroller: "Исследователь", "Lab Analyst": "Лабораторный аналитик", "Field Researcher": "Полевой исследователь", "Apex Smartasaurus": "Высший Smartasaurus" },
  ar: { Scroller: "مستكشف", "Lab Analyst": "محلل مختبر", "Field Researcher": "باحث ميداني", "Apex Smartasaurus": "سمارتاسورس القمة" },
};

export function homepageCountCopy(lang: SupportedLang): CountCopy {
  return COUNT_COPY[lang];
}

export function localizedRankName(lang: SupportedLang, source: string): string {
  return RANK_NAMES[lang][source] ?? source;
}

export function homepageNewsletterCopy(lang: SupportedLang): HomepageNewsletterCopy {
  return NEWSLETTER_COPY[lang];
}
type RankProgressCopy = {
  wins: (n: number) => string;
  toNext: (pts: number, rank: string) => string;
  top: string;
};

const RANK_PROGRESS: Record<SupportedLang, RankProgressCopy> = {
  en: { wins: (n) => `${n} ${n === 1 ? "win" : "wins"}`, toNext: (p, r) => `${p} XP to ${r}`, top: "Top rank" },
  fr: { wins: (n) => `${n} ${n === 1 ? "victoire" : "victoires"}`, toNext: (p, r) => `encore ${p} XP pour ${r}`, top: "Rang suprême" },
  es: { wins: (n) => `${n} ${n === 1 ? "acierto" : "aciertos"}`, toNext: (p, r) => `faltan ${p} XP para ${r}`, top: "Rango máximo" },
  pt: { wins: (n) => `${n} ${n === 1 ? "acerto" : "acertos"}`, toNext: (p, r) => `faltam ${p} XP para ${r}`, top: "Nível máximo" },
  de: { wins: (n) => `${n} ${n === 1 ? "Treffer" : "Treffer"}`, toNext: (p, r) => `noch ${p} XP bis ${r}`, top: "Höchster Rang" },
  sv: { wins: (n) => `${n} ${n === 1 ? "rätt" : "rätt"}`, toNext: (p, r) => `${p} XP kvar till ${r}`, top: "Högsta rang" },
  hi: { wins: (n) => `${n} जीत`, toNext: (p, r) => `${r} तक ${p} XP बाकी`, top: "सर्वोच्च रैंक" },
  zh: { wins: (n) => `答对 ${n} 次`, toNext: (p, r) => `距${r}还差 ${p} XP`, top: "最高等级" },
  ru: { wins: (n) => `побед: ${n}`, toNext: (p, r) => `ещё ${p} XP до «${r}»`, top: "Высший ранг" },
  ar: { wins: (n) => `${n} فوز`, toNext: (p, r) => `${p} XP حتى ${r}`, top: "أعلى رتبة" },
};

export function rankProgressCopy(lang: SupportedLang): RankProgressCopy {
  return RANK_PROGRESS[lang] ?? RANK_PROGRESS.en;
}

/** Already-localized output from this file - never English source. */
export const homepageLocalizedValues = (): Record<string, unknown>[] => [NEWSLETTER_COPY, RANK_NAMES];
